/**
 * @file SpacingFields.tsx
 * @description Группа «Отступы сайта» вкладки «Кнопки и карточки» (phase19.16
 * §7.13 «Отступы»; партия SB120, этап 2 — uiux-sb120-stage2 §2, §10 вопрос 5;
 * данные db-design-sb120-stage2 §1 `spacing.density` числом, §4):
 *
 * ```
 * Плотность    ├────●──┤ [ 1,00 ] ×  [Компактно 0,8][Обычно 1][Просторно 1,25]
 *  Меняет отступы «как у сайта» и поля по краям. Ваши числа в блоках не меняет
 *  (на этой странице — 12).
 * На телефоне  ├──●────┤ [ 60 ] %    ↺ 60 %
 *  Доля от ПК: блоки «как у сайта», шапка, подвал, каталог и блог на телефоне.
 * ```
 *
 * Плотность — множитель шкалы `--site-space-*`: меняет только «как у сайта»
 * (решение 2026-10-19). Счётчик своих чисел — по блокам открытой в
 * конструкторе страницы (`ownSpaceCount`); вне конструктора — без числа.
 * «На телефоне» — `spacing.phoneFactor` (с этапа 1 в теме, в кабинете не
 * было выведено — TD-417).
 */

import { SECTION_LOOK_LIMITS, SITE_THEME_LIMITS, type SiteTheme } from "@chaiberi/shared";
import { useContext } from "react";

import { NumberSliderField } from "../../site-fields/NumberSliderField";
import { BuilderContext } from "../../site-builder/builder-context";
import { DENSITY_PRESETS, DENSITY_RECOMMENDED } from "../../../lib/site-view/appearance-numbers";
import { ownSpaceCount } from "../../../lib/site-builder/look-outside";

type Spacing = SiteTheme["spacing"];

const { density: D } = SITE_THEME_LIMITS;
const PF = SECTION_LOOK_LIMITS.phoneFactor;
/** «На телефоне» не меньше 30 % (uiux-sb120-stage2 §5): мельче отступы слипаются. */
const PHONE_MIN = 0.3;

interface Props {
  spacing: Spacing;
  onSpacing: (patch: Partial<Spacing>) => void;
  onPreview?: (patch: Partial<Spacing>) => void;
}

/** «… (на этой странице — 12)» — только когда страница открыта в конструкторе. */
function useOwnNumbersText(): string {
  const page = useContext(BuilderContext)?.pages.page;
  if (!page) return "";
  return ` (на этой странице — ${ownSpaceCount(page.blocks)})`;
}

export function SpacingFields({ spacing, onSpacing, onPreview }: Props) {
  const own = useOwnNumbersText();
  return (
    <div className="flex min-w-0 flex-col gap-4">
      <NumberSliderField
        label="Плотность"
        value={spacing.density}
        inherit={{ value: D.default, label: "×1" }}
        min={D.min}
        max={D.max}
        step={0.05}
        unit="×"
        display={{ factor: 1, decimals: 2 }}
        presets={DENSITY_PRESETS}
        recommended={DENSITY_RECOMMENDED}
        hint={`Меняет отступы «как у сайта» и поля по краям. Ваши числа в блоках не меняет${own}.`}
        onChange={(v) => onSpacing({ density: v ?? D.default })}
        onPreview={onPreview ? (v) => onPreview({ density: v ?? D.default }) : undefined}
      />
      <NumberSliderField
        label="На телефоне"
        value={spacing.phoneFactor}
        inherit={{ value: PF.default, label: `${Math.round(PF.default * 100)} %` }}
        min={PHONE_MIN}
        max={PF.max}
        step={0.01}
        unit="%"
        display={{ factor: 100, decimals: 0 }}
        hint="Доля от ПК: блоки «как у сайта», шапка, подвал, каталог и блог на телефоне."
        onChange={(v) => onSpacing({ phoneFactor: v ?? PF.default })}
        onPreview={onPreview ? (v) => onPreview({ phoneFactor: v ?? PF.default }) : undefined}
      />
    </div>
  );
}
